import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { logger } from './logger.js';
import { DEFAULT_PLATFORM_BRAND, isPrerender } from './default-brand.js';
import type { PlatformBrand } from '@kilat/shared';

const BrandContext = createContext<PlatformBrand | null>(null);

export function PlatformBrandProvider({
  host,
  children,
}: {
  readonly host?: string;
  readonly children: ReactNode;
}) {
  // Seed the DEFAULT brand SYNCHRONOUSLY — at runtime as well as under the
  // build-time prerender browser (ADR-0024 slice 2b part B).
  //
  // This used to seed only during prerender and start at `null` at runtime, so
  // hydration THREW AWAY the prerendered page: every marketing route rendered
  // `<Splash />` until a Firestore read returned, meaning a visitor watched a
  // splash while firebase downloaded and a document round-tripped. Measured on
  // the live site 2026-08-22: first contentful paint 8.2s and Lighthouse 56
  // against a >=90 budget. The pre-render was doing real work and hydration
  // was discarding it.
  //
  // The default IS this brand's own seeded configuration, so first paint is
  // CORRECT rather than merely fast. The live read below still runs and refines
  // it — that is what multi-brand hosting needs (ADR-0017) — it just no longer
  // holds the whole site hostage while it does.
  const [brand, setBrand] = useState<PlatformBrand>(DEFAULT_PLATFORM_BRAND);

  useEffect(() => {
    // Prerender: brand is already the synchronous default — never touch Firestore.
    if (isPrerender()) return;

    let cancelled = false;
    let unsubLive: (() => void) | null = null;

    (async () => {
      try {
        // firebase is imported HERE, not at module scope.
        //
        // Statically importing the repo pulled firestore and App Check into the
        // marketing ENTRY chunk — 1.87 MB that every visitor downloaded before
        // the render-blocking stylesheet could win the pipe, which is what put
        // first contentful paint at 8-11s on simulated mobile. Nothing on this
        // site needs firebase to paint: the brand is seeded synchronously above,
        // and this read only REFINES it for multi-brand hosting (ADR-0017).
        //
        // Deferring it costs nothing a visitor can see and takes the whole
        // Firestore client off the critical path.
        const [{ platformBrandsRepo }, { loadBrand, resolveBrandIdForHost }] = await Promise.all([
          import('./repositories/platform-brands-repo.js'),
          import('./brand-resolver.js'),
        ]);
        const resolvedHost = host ?? window.location.hostname;
        const brandId = await resolveBrandIdForHost(resolvedHost);
        const initial = await loadBrand(brandId);
        if (cancelled) return;
        setBrand(initial);
        unsubLive = platformBrandsRepo.snapshot(brandId, (next) => {
          if (cancelled || !next) return;
          setBrand(next);
        });
      } catch (e) {
        // The whole marketing site gates first paint on this read. If it
        // throws (offline, Firestore outage, missing seed) we must NOT sit
        // on the loading spinner forever — that's a silent dead-end. Flip to
        // an explicit, retryable error surface instead (no-dead-ends).
        // Degraded, not broken: this brand's seeded configuration is already
        // on screen, so there is nothing to gate and nobody to strand.
        logger.error('PlatformBrandProvider live read failed', { error: String(e) });
      }
    })();

    return () => {
      cancelled = true;
      if (unsubLive) unsubLive();
    };
  }, [host]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

/**
 * Pre-brand, pre-i18n error surface for a failed brand-resolution read.
 *
 * This renders ABOVE the `<I18nProvider>`, so `useI18n()` isn't available yet
 * — we pick copy from the device language directly (same en/ms/zh/ta set the
 * i18n layer supports). Kilat design tokens ARE available (globals.css loads in
 * main.tsx regardless of brand), so this stays on-palette without hard-coded
 * hex. Agent-authored ms/zh/ta below — flag for native review.
 */
// The brand-gate error surface that used to live here is gone.
//
// It existed because first paint depended on a Firestore read: if that read
// failed there was nothing to show, so it offered a retry. Now the seeded
// default brand renders immediately and a failed live read is a degraded
// state, not a dead end — an error screen over a working site would be the
// worse outcome. Removed with its locale copy table rather than left
// unreachable.

export function usePlatformBrand(): PlatformBrand {
  const v = useContext(BrandContext);
  if (!v) throw new Error('usePlatformBrand must be used inside <PlatformBrandProvider>');
  return v;
}
